import { useCalibrations, useModuleEndpoint, useModules, useRecordCalibration } from '@ctcells/api'
import { dataSourceFor } from '@ctcells/core'
import { errorMessage } from '@ctcells/i18n'
import { useParams } from 'react-router'

import { activeModules } from '../moduleService.ts'
import { useScreenAccess } from '../screenAccess.ts'
import { ScreenLoading, ScreenState } from '../ScreenState.tsx'

import { CalibrationDetailScreen } from './CalibrationDetailScreen.tsx'
import { ModuleBrokerContainer } from './ModuleBrokerContainer.tsx'
import { ModuleDetailScreen } from './ModuleDetailScreen.tsx'
import { ModuleInitializeContainer } from './ModuleInitializeContainer.tsx'
import { moduleName, toRegistryRow } from './moduleRows.ts'
import type { AdminTabId } from '../admin/adminTabs.ts'

import { ModulesScreen } from './ModulesScreen.tsx'

/**
 * 시스템 › 모듈 컨테이너.
 *
 * 🔴 **이 화면은 컨테이너 없이 라우팅돼 있었다**(2026-09-18 발견). 그래서
 *    `dataSourceFor('modules')` 를 아예 타지 않았고, `modules: 'live'` 인데도 **표 전체가 목**이었다.
 *    eric 이 본 「IP 10.20.0.11 · 연결 0.4s · 버전 0.9.1」은 서버가 준 적 없는 값이다.
 *    같은 함정이 `ModuleDetailScreen` · `CalibrationDetailScreen` 에도 있었다.
 *
 * 🔴 **`access` 를 하드코딩하지 않는다.** 예전엔 라우트가 `DEMO_ACCESS` 를 박아서 권한 판정까지
 *    가짜였다. 실세션(`useScreenAccess`)에서 받는다 — 노출만 가르고 집행은 서버 403 이다.
 *
 * 🔴 **갈리는 층은 `dataSourceFor('modules')` 하나다.** 어댑터·MSW 로 내리지 않는다.
 */
/**
 * @param adminTab 열린 무리 탭. 🔴 **라우트가 넘긴다** — 탭이 곧 주소다.
 *   **안 넘기면 무리 탭 줄을 안 그린다**(`useGroupTabLayout` 주석). 장비 관점 콘솔이 그렇게 쓴다.
 */
export interface ModulesContainerProps {
  adminTab?: AdminTabId | undefined
  /** 오른쪽 판에 열린 모듈 — 그 행을 «고른 행»으로 그린다. 라우트가 넘긴다 */
  selectedModuleId?: string | undefined
}

export function ModulesContainer(props: ModulesContainerProps = {}) {
  return dataSourceFor('modules') === 'demo' ? (
    <ModulesDemo {...props} />
  ) : (
    <ModulesLive {...props} />
  )
}

/** demo 경로는 목록 props 를 안 넘긴다 = 화면 기본값(fixture)이 그대로 산다 */
function ModulesDemo({ adminTab, selectedModuleId }: ModulesContainerProps) {
  return (
    <ModulesScreen
      access={useScreenAccess()}
      selectedModuleId={selectedModuleId}
      {...(adminTab === undefined ? {} : { adminTab })}
    />
  )
}

function ModulesLive({ adminTab, selectedModuleId }: ModulesContainerProps) {
  const access = useScreenAccess()
  const modules = useModules({})

  /*
   * 🔴 **목록은 `GET /modules` 한 번이 전부다**(2026-09-20).
   *    예전에는 주소 열 하나 때문에 복수 조회 훅으로 **모듈 수만큼 더 불렀다**(N+1).
   *    그 열을 뺐으므로 호출도 걷었고, **그 훅 자체도 2026-09-21 에 지웠다**(사유는
   *    `@ctcells/api` 의 `useModuleEndpoint` 주석) — 안 그리는 값을 위해 네트워크를 쓰지 않는다.
   *    주소는 **모듈 상세**에서만 부른다(아래 `ModuleDetailLive`).
   * ⚠ 보정도 같이 걷었다. 목록에 「보정」 열이 없다 — 보정은 상세의 「보정」 탭이 쥔다.
   */

  if (modules.isPending) return <ScreenLoading />
  if (modules.isError) return <ScreenState text={errorMessage(modules.error)} tone="error" />

  /*
   * 🔴 **폐기 모듈은 표에서 뺀다**(2026-09-20 eric 지시 — 「다 받은 다음에 active 만 보이게
   *    해줘」). 실물 `GET /modules` 는 8건을 주는데 넷이 `service_state: 'terminated'` 다.
   *    그대로 그리면 **고장 난 장비와 폐기된 장비가 구분되지 않는다** — 같은 종류가 두 줄
   *    뜨고 하나는 「응답 없음」이었다.
   * ⚠ **서버가 아니라 여기서 거른다** — 목록 경로에 `include_terminated` 같은 파라미터가
   *   아직 없다(`docs/backend-requests.csv` BR-01 A안은 선택 사항으로 남아 있다).
   * ⚠ 값이 없으면 `active` 로 본다 — 있는 장비를 지우지 않는다.
   * 🔴 **「종착 포함」 토글은 없다**(eric 2026-10-22 「종착 포함 이거 버튼을 없애줘」) — 목록은 늘 운용 중 모듈만이다.
   */
  const shown = activeModules(modules.data.items)
  return (
    <ModulesScreen
      {...(adminTab === undefined ? {} : { adminTab })}
      access={access}
      modules={shown.map((m) => toRegistryRow(m))}
      selectedModuleId={selectedModuleId}
    />
  )
}

/**
 * 모듈 상세 컨테이너. 목록과 같은 자료를 쓰고 어느 행인지는 화면이 URL 로 고른다.
 * 🔴 상세도 컨테이너 없이 라우팅돼 있었다 — 같은 함정이다.
 */
export interface ModuleDetailContainerProps {
  /** 서랍(목록 오른쪽 판)으로 열렸을 때의 닫기. 없으면 전체 화면이고 「← 모듈」 줄이다 */
  onClose?: (() => void) | undefined
}

export function ModuleDetailContainer(props: ModuleDetailContainerProps = {}) {
  return dataSourceFor('modules') === 'demo' ? (
    <ModuleDetailDemo {...props} />
  ) : (
    <ModuleDetailLive {...props} />
  )
}

function ModuleDetailDemo({ onClose }: ModuleDetailContainerProps) {
  return <ModuleDetailScreen access={useScreenAccess()} onClose={onClose} />
}

function ModuleDetailLive({ onClose }: ModuleDetailContainerProps) {
  const access = useScreenAccess()
  const { moduleId } = useParams()
  const modules = useModules({})
  /*
   * 🔴 **주소를 부르는 곳은 여기 하나다.** 값은 실재하지만(`GET /modules/{id}/endpoint`)
   *    모듈마다 따로 불러야 해서 **목록에 두면 N+1** 이다. 상세는 한 대만 보므로 값이 있다.
   *
   * 🔴 **보고 있는 한 대만 부른다**(2026-09-21). 종전에는 복수 조회 훅으로 목록 전체를
   *    불렀는데 그 훅이 `Promise.allSettled` 로 **실패를 통째로 삼켰다** — 이 경로는 그때
   *    **admin 전용**이라(v0.9.4 부터 모든 역할 · `ENDPOINT_READ_ROLES`) operator 는 전부 403 인데도
   *    쿼리가 «성공 + 빈 객체»가 됐고, 화면에는 그냥 `—` 가 떴다. 「값이 없다」와
   *    「볼 권한이 없다」가 구분되지 않던 자리다. 단건 훅은 **403 을 그대로 올려 준다.**
   *    ⚠ 덤으로 N+1 도 사라진다 — 상세는 한 대만 보는 화면이다.
   * ⚠ 어느 모듈인지는 **주소(URL)가 정한다.** 화면도 같은 `moduleId` 로 행을 고른다.
   */
  const endpoint = useModuleEndpoint({ moduleId })

  if (modules.isPending) return <ScreenLoading />
  if (modules.isError) return <ScreenState text={errorMessage(modules.error)} tone="error" />

  /*
   * 🔴 **「초기화」는 서랍(통합 콘솔)에서만 싣는다**(eric 2026-09-28 「모듈 초기화 버튼 만들어줘」).
   *    `onClose` 가 그 축이다 — 장비 관점 콘솔은 같은 컨테이너를 전체 화면(`onClose` 없음)으로 쓰고,
   *    그 앱은 고치지 않는다. `key` 는 모듈이 바뀌면 앞 모듈의 결과(202 · 실패)를 버리려는 것이다.
   */
  const snapshot = modules.data.items.find((m) => m.module_id === moduleId)
  /*
   * 🔴 **「모듈 관리」 카드(모듈 ID 바꾸기 · 모듈 지우기)는 없다**(eric 2026-10-22 「모듈 관리 카드 제거해줘」).
   *    지우기 · ID 바꾸기 훅도 여기서 부르지 않는다 — API 훅(`useDeleteModule` · `useChangeModuleIdentity`)은 `@ctcells/api` 에 남아 있다
   */
  const controlSlot =
    onClose === undefined || snapshot === undefined ? undefined : (
      <ModuleInitializeContainer
        key={snapshot.module_id}
        module={snapshot}
        // 같은 종류가 여러 대라 ID 를 같이 적는다(상세 제목 규약과 같다)
        moduleName={`${moduleName(toRegistryRow(snapshot))}(${snapshot.module_id})`}
      />
    )

  return (
    <ModuleDetailScreen
      access={access}
      {...(controlSlot === undefined ? {} : { controlSlot })}
      /*
       * 「연결」 카드의 「브로커 주소」 줄(`IFC-RO-78` · admin) — 「제어」 카드와 같은 조건(서랍 · live · 그 모듈 스냅샷)에서만.
       * 종전 「브로커 접속」 카드는 걷었다(eric 2026-10-22 「브로커 접속 빼고.. 브로커 주소..를 연결에 넣고. 계정 비밀번호 접속 내용 빼줘」).
       * `key` 는 모듈이 바뀌면 앞 모듈의 창 · 결과(202 교체 행)를 버리려는 것이다
       */
      {...(onClose === undefined || snapshot === undefined
        ? {}
        : {
            brokerRow: (
              <ModuleBrokerContainer
                key={`broker-${snapshot.module_id}`}
                moduleId={snapshot.module_id}
              />
            ),
          })}
      /*
       * 🔴 **주소를 못 받은 «사유»를 나른다.** 못 받은 것을 `—`(값 없음)로 접지 않는다 —
       *    operator 에게는 403 이 **정상 상태**이고, 그 사실이 화면에 그대로 서야 한다.
       */
      {...(endpoint.isError ? { addressNotice: errorMessage(endpoint.error) } : {})}
      /*
       * ⚠ **상세는 거르지 «않는다».** 목록에서 뺀 것과 다른 판단이다 — 주소를 직접 친
       *   사람(감사 이력의 링크 · 북마크)이 「왜 이 장비가 없나」를 확인할 수 있어야 한다.
       *   들어오는 길이 목록뿐이라 폐기 모듈이 여기 저절로 뜨는 일은 없다.
       */
      modules={modules.data.items.map((m) =>
        toRegistryRow(m, m.module_id === moduleId ? endpoint.data : undefined),
      )}
      onClose={onClose}
    />
  )
}

/**
 * 보정 상세 컨테이너.
 * ✅ **2026-09-20 실물 5건을 넣어 확인했다**(`fe-20260920-cal-r1-*`). `calibration` 도메인은 `live` 다.
 *   그 자리에서 드러난 것 둘을 고쳤다 — ⓐ 서버가 `valid_until` 을 **키째 빼서** 보내는 것,
 *   ⓑ 실물 경로가 `DEMO_NOW` 로 임박을 재던 것.
 */
export function CalibrationDetailContainer() {
  return dataSourceFor('calibration') === 'demo' ? (
    <CalibrationDetailDemo />
  ) : (
    <CalibrationDetailLive />
  )
}

function CalibrationDetailDemo() {
  return <CalibrationDetailScreen access={useScreenAccess()} />
}

function CalibrationDetailLive() {
  const access = useScreenAccess()
  const calibrations = useCalibrations({})
  const record = useRecordCalibration()

  if (calibrations.isPending) return <ScreenLoading />
  if (calibrations.isError)
    return <ScreenState text={errorMessage(calibrations.error)} tone="error" />

  // 🔴 `now` 는 실물 경로의 필수 인자다 — 위 `ModuleDetailScreen` 과 같은 사유.
  return (
    <CalibrationDetailScreen
      access={access}
      calibrations={calibrations.data.items}
      now={new Date().toISOString()}
      /*
       * ⚠ **모듈 상세와 같은 명령·같은 대화다.** 이 화면은 종류가 이미 정해져 있다는 것만 다르고,
       *   그 차이는 화면이 `fixedType` 으로 넘긴다 — 여기서 갈리는 것은 없다.
       */
      onRecordCalibration={(submit, onSuccess) => {
        record.mutate(submit, { onSuccess })
      }}
      {...(record.error === null ? {} : { recordCalibrationError: errorMessage(record.error) })}
      recordingCalibration={record.isPending}
    />
  )
}
